import { useLazyQuery, useMutation } from "@apollo/client";
import { ONLY_LETTERS_AND_DASH_REGEX, USER_TOKEN_PERSIST } from "@constants/global";
import {
  Button,
  Checkbox,
  Input,
  StepperV2,
  type StepperV2Step,
} from "components/ui/v2";
import { gqlUser, gqlVendor } from "gql";
import { ICreateVendorSaasInput } from "gql/Vendor/mutations";
import { Loader2 } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, useForm } from "react-hook-form";
import { ECompanySegment, IVendor, companySegment } from "types/Vendor.types";
import { getQueryOperator } from "utils/helpers";
import useToast from "utils/hooks/useToast";
import { termsAndConditionsEn } from "utils/terms-en";
import { termsAndConditions } from "utils/terms-es";
import { buildFirstLoginUrl } from "utils/safe-navigation";
import RadioBoxAurora from "./RadioBoxAurora";

interface IOrganizationFormProps {
  callback?: (error?: string) => void;
}
const totalSteps = 4;

interface IForm {
  name: string;
  employees: number;
  companySegment: ECompanySegment;
  aceptPolicy: boolean;
  reCaptcha: string;
}

const OrganizationFormAurora = (props: IOrganizationFormProps) => {
  const { toast } = useToast();
  const [error, setError] = useState<string>("");
  const { callback } = props;
  const [step, setStep] = useState<number>(1);
  const { t, lang } = useTranslation("vendor");
  const [captcha, setCaptcha] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [completed, setCompleted] = useState<boolean>(false);
  const [domain, setDomain] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(true);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
    trigger,
    setValue,
  } = useForm<IForm>({
    defaultValues: {
      name: "",
      employees: 5,
      companySegment: companySegment.SOFTWARE["name"],
      aceptPolicy: false,
    },
  });

  const [validateVendorName, { loading: validateVendorNameLoading }] =
    useLazyQuery(gqlVendor.queries.VALIDATE_VENDOR_NAME, {
      onCompleted: ({ validateVendorName }) => {
        if (validateVendorName) {
          setError(t("El nombre de la empresa ya existe, intenta otro"));
          setCaptcha(null);
          recaptchaRef.current?.reset();
        } else {
          setStep(step + 1);
        }
      },
      onError: ({ message }) => {
        toast.error(message);
      },
    });

  const [createVendorSaas, { loading: createVendorSaasLoading }] = useMutation<
    { createVendorSaas: IVendor },
    { createVendorSaasInput: ICreateVendorSaasInput }
  >(gqlVendor.mutations.CREATE_VENDOR_SASS, {
    refetchQueries: [
      getQueryOperator(gqlUser.queries.GET_FULL_USER),
      getQueryOperator(gqlUser.queries.USER_ONBOARDING),
    ],
    awaitRefetchQueries: true,
    onCompleted({ createVendorSaas }) {
      const { id, domains } = createVendorSaas;
      if (id) {
        setCompleted(true);
        setStep(step + 1);
        if (domains && domains.length > 0) {
          setDomain(domains[0]);
        }
      }
    },
    onError: ({ message }) => {
      if (callback) {
        callback(message);
      }
    },
  });

  const handleFinish = (data: IForm) => {
    createVendorSaas({
      variables: {
        createVendorSaasInput: {
          ...data,
        },
      },
    });
  };

  const handleEmployeeClick = (value: string | number) => {
    if (typeof value === "number") {
      setValue("employees", value);
    }
  };

  const handleCompanySegmentClick = (value: ECompanySegment) => {
    setValue("companySegment", value);
  };

  const handleNext = async () => {
    if (step < totalSteps) {
      if (step === 1) {
        const ok = await trigger("name");
        if (ok) {
          validateVendorName({
            variables: {
              name: watch("name"),
              captcha: captcha || "",
            },
          });
          setError("");
        } else {
          setError(t("Debes ingresar un nombre de empresa válido"));
        }
      }

      if (step === 2) {
        const ok = await trigger("employees");
        if (ok) {
          setStep(step + 1);
          setError("");
        } else {
          setError(t("Debes seleccionar el numero de empleados"));
        }
      }

      if (step === 3) {
        const ok = await trigger("companySegment");
        if (ok) {
          setStep(step + 1);
          setError("");
        } else {
          setError(t("Debes seleccionar una ndustria"));
        }
      }
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleCaptcha = (data: string) => {
    setCaptcha(data);
  };

  useEffect(() => {
    if (completed) {
      setTimeout(() => {
        setCreating(false);
      }, 180000);
    }
  }, [completed]);

  const stepperSteps: StepperV2Step[] = [
    t("Organización"),
    t("Tamaño"),
    t("Tipo"),
    t("Términos"),
  ].map((title, i) => {
    const idx = i + 1;
    return {
      id: String(idx),
      title,
      status: idx < step ? "completed" : idx === step ? "current" : "pending",
    };
  });

  return (
    <div className="flex w-full flex-col gap-5">
      {!completed && <StepperV2 steps={stepperSteps} className="justify-center" />}

      <div className="w-full">
        {step === 1 && (
          <div
            className="animate__animated animate__fadeInRight animate__faster w-full"
            id="step1"
          >
            <h3 className="mb-1 text-center text-2xl font-bold">
              {t("Organización")}
            </h3>
            <label className="mb-3 block text-center text-sm text-slate-500 dark:text-zinc-400">
              {t("Ingresa el nombre de tu organización")}
            </label>

            <Controller
              name="name"
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: ONLY_LETTERS_AND_DASH_REGEX,
                  message: t(
                    "El nombre de la organización solo puede contener letras y guiones"
                  ),
                },
              }}
              render={({ field }) => (
                <div className="flex items-stretch gap-2">
                  <div className="flex-1">
                    <Input {...field} placeholder={t("ej: groowcity-x")} />
                  </div>
                  <span className="flex shrink-0 items-center rounded-2xl border border-slate-200/80 bg-slate-50 px-3 text-sm text-slate-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
                    .groowcity.com
                  </span>
                </div>
              )}
            />
            <small className="mt-1 block text-emerald-600 dark:text-emerald-400">
              * {t("Solo puedes usar letras y guiones")}
            </small>
            <div className="mt-3 flex items-center justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6Ldu9p4UAAAAANobJCf3Weyt5ptMNMsk9OlYaZoh"
                hl={lang}
                onChange={handleCaptcha}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div
            className="animate__animated animate__fadeInRight animate__faster w-full"
            id="step2"
          >
            <h3 className="mb-1 text-center text-2xl font-bold">{t("Tamaño")}</h3>
            <label className="mb-3 block text-center text-sm text-slate-500 dark:text-zinc-400">
              {t("Número de empleados")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <RadioBoxAurora
                icon="user"
                title={t("De 1 a 5")}
                value={5}
                currentValue={watch("employees")}
                onClick={handleEmployeeClick}
              />
              <RadioBoxAurora
                icon="users"
                title={t("De 6 a 15")}
                value={15}
                currentValue={watch("employees")}
                onClick={handleEmployeeClick}
              />
              <RadioBoxAurora
                icon="users"
                title={t("De 16 a 50")}
                value={50}
                currentValue={watch("employees")}
                onClick={handleEmployeeClick}
              />
              <RadioBoxAurora
                icon="users"
                title="+51"
                value={99999}
                currentValue={watch("employees")}
                onClick={handleEmployeeClick}
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div
            className="animate__animated animate__fadeInRight animate__faster w-full"
            id="step3"
          >
            <h3 className="mb-1 text-center text-2xl font-bold">{t("Tipo")}</h3>
            <label className="mb-3 block text-center text-sm text-slate-500 dark:text-zinc-400">
              {t("¿A qué industria pertenece?")}
            </label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {Object.keys(companySegment).map((segment) => (
                <RadioBoxAurora
                  key={companySegment[segment].name}
                  icon={companySegment[segment].icon}
                  title={t(companySegment[segment].name)}
                  value={companySegment[segment].name}
                  currentValue={watch("companySegment")}
                  onClick={(value) =>
                    handleCompanySegmentClick(value as ECompanySegment)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div
            className="animate__animated animate__fadeInRight animate__faster w-full"
            id="step4"
          >
            <h3 className="mb-1 text-center text-2xl font-bold">
              {t("Términos")}
            </h3>
            <label className="mb-3 block text-center text-sm text-slate-500 dark:text-zinc-400">
              {t("Términos y condiciones")}
            </label>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  lang === "en" ? termsAndConditionsEn : termsAndConditions,
              }}
              style={{ height: 240, overflowY: "auto" }}
              className="mb-4 rounded-2xl border border-slate-200/80 bg-slate-50 p-5 text-sm dark:border-zinc-800 dark:bg-zinc-900"
            />
            <Controller
              name="aceptPolicy"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="flex items-center justify-center">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                      trigger("aceptPolicy");
                    }}
                    label={t("ACEPT_TERMS_AND_CONDITIONS")}
                  />
                </div>
              )}
            />
            <Controller
              name="reCaptcha"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="mt-3 flex items-center justify-center">
                  <ReCAPTCHA
                    {...field}
                    sitekey="6Ldu9p4UAAAAANobJCf3Weyt5ptMNMsk9OlYaZoh"
                    hl={lang}
                    onChange={(value) => {
                      field.onChange(value);
                      trigger("reCaptcha");
                    }}
                  />
                </div>
              )}
            />
          </div>
        )}

        {step === 5 && completed && (
          <div className="animate__animated animate__fadeInRight animate__faster flex w-full flex-col items-center justify-center gap-3 text-center">
            <h3 className="text-2xl font-bold">
              {t("¡Excelente! Estamos Preparando Tu Nueva Organización")}
            </h3>
            <p className="text-sm text-slate-500 dark:text-zinc-400">
              {t(
                "¡Descubre tu nuevo mundo en Groowcity! Haz clic en 'Siguiente' y comienza tu aventura"
              )}
            </p>
            {creating && (
              <span className="text-sm font-semibold">
                {t("Creando tu organización, esto puede tardar unos minutos")}
              </span>
            )}
            <Button
              width="full"
              size="lg"
              disabled={creating}
              onClick={() => {
                window.open(
                  buildFirstLoginUrl(
                    domain,
                    localStorage.getItem(USER_TOKEN_PERSIST) ?? ""
                  )
                );
              }}
            >
              {creating ? (
                <>
                  {t("Creando organización")}{" "}
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                t("Ir a mi organización")
              )}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <small className="block text-center text-destructive">{error}</small>
      )}

      {!completed && (
        <div className="flex items-center justify-center gap-3">
          {step > 1 && (
            <Button variant="secondary" width="full" onClick={handlePrev}>
              {t("Anterior")}
            </Button>
          )}
          {step < totalSteps && (
            <Button
              width="full"
              size="lg"
              onClick={handleNext}
              loading={validateVendorNameLoading}
              disabled={step === 1 && !captcha}
            >
              {t("Siguiente")}
            </Button>
          )}
          {step === totalSteps && (
            <Button
              width="full"
              size="lg"
              onClick={handleSubmit(handleFinish)}
              disabled={!isValid}
              loading={createVendorSaasLoading}
            >
              {t("Finalizar y crear")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationFormAurora;
